import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { Product } from '../../../../../models/product.model';
import { OrderItem } from '../../../../../models/order-item.model';
import { SKU } from '../../../../../models/sku.model';
import {OrderItemSku} from '../../../../../models/order-item-sku.model';
import { Currency } from '../../../../../interfaces/currency';
import { ProductService } from '../../../../../services/product.service';
import { computeGroupAggregatedQuantity } from '../../../../../shared/helpers';


@Component({
    selector: 'app-product-order-item-price',
    templateUrl: './product-order-item-price.component.html',
    styleUrls: ['./product-order-item-price.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager
})
export class ProductOrderItemPriceComponent {

    private _orderItem: OrderItem;

    @Input() fontSize = ''; // normal (24px) | small (20px) |  tiny (16px)

    @Input() viewType: string;

    @Input() sku: SKU;

    @Input() product: Product;

    @Input() modal = false;

    @Input() position: 'relative' | 'absolute' = 'absolute';

    @Input() set orderItem(val: OrderItem) {
        this._orderItem = val;
        if (this._orderItem) {
            this.product = this._orderItem.product;
        }
    }


    get price(): number {        
        if (!this.product) {
            return 0;
        }

        // Compute price from tiers based on current cart quantity
        // so the displayed price matches the highlighted tier
        const tierPrice = this.priceFromTiers;
        if (tierPrice !== null) {
            return tierPrice;
        }

        if (this._orderItem && this.sku) {
            return this._orderItem.getProductPrice(this.sku);
        }

        if (this.sku) {
            if (this._orderItem) {
                return this._orderItem.getProductPrice(this.sku);
            }
            return this.sku.getPrice();
        }

        if (!this._orderItem) {
            return this.product.getPrice(this.sku);
        }

        return this._orderItem.getProductPrice(this.sku);

    }

    private get priceFromTiers(): number | null {
        const currentSku = this.currentSKU;
        if (!currentSku) {
            return null;
        }

        if (!currentSku.hasPricingTiers && !currentSku.hasPriceRunOnRange) {
            return null;
        }

        const tiers = ProductService.computePriceTiers(this.product, currentSku, this.quantityForTiers);

        if (tiers.priceTiers?.length > 0) {
            const selectedTier = tiers.priceTiers.find(t => t.selected);
            if (selectedTier) {
                return selectedTier.price;
            }
        }

        if (tiers.runOnPrices?.length > 0) {
            const selectedRunOn = tiers.runOnPrices.find(t => t.selected);
            if (selectedRunOn) {
                return selectedRunOn.pricing;
            }
        }

        return null;
    }

    get pkgQty(): string {
        const pkgQty =  this.currentSKU.packageQuantity;
        if (pkgQty ===  1) {
            return 'Each';
        } else if ( pkgQty > 1) {
            return `${pkgQty} / Pack`;
        }

        return '';

    }

    get quantityForTiers(): number {
        if (this.product.isBuyingWindow) {
            const groupAggregated = computeGroupAggregatedQuantity(this.product);
            const base = groupAggregated > 0 ? groupAggregated : 0;
            return base + (this._orderItem?.quantityDelta || 0);
        }

        if (this.product.hasVariations) {
            return this._orderItem?.quantity || 0;
        }

        return this.orderItemSKU?.quantity || this._orderItem?.quantity || 0;
    }

    get discount(): number {
        if (!this.hasValidDiscount) {
            return 0;
        }
        if (this._orderItem) {
            return this._orderItem.getProductDiscount(this.sku);
        }

        return this.product ? this.product.discount : 0;
    }


    get originalPrice(): number {
        return this.product.getOriginalPrice(this.sku);
    }

    get currentSKU() {
        return this.sku ? this.sku : this.product.skus[0];
    }

    get orderItemSKU(): OrderItemSku {
        if (!this.sku || !this._orderItem) {
            return null;
        }

        return this._orderItem.skus.find( s => s.sku_id === this.sku.id);
    }

    get classes(): string {
        return `${this.orientation} ${this.fontSize}`;
    }

    get orientation(): string {
        return  this.viewType === 'grid-item' ? 'horizontal' : 'vertical';
    }

    get hasVariations(): boolean {
        return this.product.hasVariations;
    }

    get showPriceVariations(): boolean {
        return this.viewType === 'grid-item' && this.hasVariations;
    }

    get priceVariations(): {min?: number, max?: number} {
        return this.product.priceVariations;
    }

    get canShowPriceTiers(): boolean {
        return !(this.product.hasVariations && this.viewType === 'grid-item');
    }

    get currency(): Currency {
        if (this.orderItem?.product) {
            return this.orderItem.product.currency;
        }

        if (this.product) {
            return this.product.currency;
        }

        return null;
    }

    get isItemCut(): boolean {
        return !!(this._orderItem?.item_cut || this.product?.item_cut);
    }

    get hasDiscountError(): boolean {
        if (this._orderItem) {
            return this._orderItem.hasDiscountErrors;
        }

        return false;
    }

    get hasValidDiscount(): boolean {
        if (!this._orderItem) {
            return false;
        }

        if (this.isItemCut) {
            return false;
        }

        return this._orderItem.totalDiscount > 0 && !this.hasDiscountError;
    }


    get showBothPrices(): boolean {
        return this.hasValidDiscount;
    }

    get amountOfShipments(): number {
        return this._orderItem?.deliveries?.length || 0;
    }
}
